import React, { useState, useEffect, useMemo } from "react";
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  DataTableSkeleton,
  Button,
  Pagination,
} from "@carbon/react";
import { Add, TrashCan } from "@carbon/icons-react";
import "./chatManagerDataTable.scss";
import AddItemModal from "../addItemModal/AddItemModal";
import ChatMembersModal from "../chatMembersModal/ChatMembersModal";
import { useTranslation } from "react-i18next";
import DeleteItemModal from "../deleteItemModal/DeleteItemModal";

export default function ChatManagerDataTable({
  guid,
  client,
  data,
  pending,
  isMobile,
}) {
  const [openAddChatModal, setOpenAddChatModal] = useState(false);

  const [openEditChatModal, setOpenEditChatModal] = useState(false);

  const [openChatRemoval, setOpenChatRemoval] = useState(false);
  const [removingChat, setRemovingChat] = useState(null);

  const [editChatId, setEditChatId] = useState();
  const [editChatName, setEditChatName] = useState();

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 3,
  });

  const { t } = useTranslation();

  const onDelete = (item) => {
    setRemovingChat({ guid, client, chat: item.name, id: item.chatid });
    setOpenChatRemoval(true);
  };

  useEffect(() => {
    if (editChatId) {
      setOpenEditChatModal(true);
    }
  }, [editChatId]);

  function mapToTableData(data) {
    const headers = [
      {
        header: "main.chat.chatManagerTable.name",
        key: "name",
      },
      {
        header: "main.chat.chatClientsTable.actions",
        key: "actions",
      },
    ];

    const rows = Array.isArray(data)
      ? data.map((item) => ({
          name: String(item.name),
          id: String(item.chatid),
          actions: (
            <div className="chatManagerButtons">
              <Button
                size="sm"
                kind="tertiary"
                onClick={() => {
                  setEditChatId(item.chatid);
                  setEditChatName(item.name);
                }}
                className="chatManagerButtons_edit"
              >
                {t("main.chat.chatManagerTable.edit")}
              </Button>
              <Button
                hasIconOnly
                renderIcon={TrashCan}
                kind="tertiary"
                size="sm"
                iconDescription={t("main.chat.chatClientsTable.deleteDesc")}
                onClick={() => onDelete(item)}
              />
            </div>
          ),
        }))
      : [];

    return { headers, rows };
  }

  const { headers, rows: allRows } = mapToTableData(data);

  const paginatedRows = useMemo(() => {
    const start = (pagination.page - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    return allRows.slice(start, end);
  }, [allRows, pagination]);

  const names = data?.map((c) => c.name);

  return (
    <div className="main_chat_manager">
      {pending ? (
        <DataTableSkeleton
          aria-label="sample table"
          headers={[
            {
              header: t("main.chat.chatManagerTable.name"),
              key: "name",
            },
            {
              header: "",
              key: "edit",
            },
          ]}
          showHeader
          showToolbar
        />
      ) : (
        <DataTable
          size="lg"
          headers={headers}
          rows={paginatedRows}
          isSortable
          render={({ rows, headers }) => (
            <TableContainer
              title={
                <div className="main_chat_info_title">
                  <span>{`${t("main.chat.chatManagerTable.title")}`}</span>
                  <div className="main_relations_button_set">
                    <Button
                      kind="primary"
                      hasIconOnly={isMobile}
                      iconDescription={t(
                        "main.chat.chatManagerTable.addChatButton",
                      )}
                      renderIcon={Add}
                      onClick={() => setOpenAddChatModal(true)}
                    >
                      {t("main.chat.chatManagerTable.addChatButton")}
                    </Button>
                  </div>
                </div>
              }
              description={`${t("main.chat.chatManagerTable.description")}`}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    {headers.map((header) => {
                      return (
                        <TableHeader key={header.key}>
                          {t(header.header)}
                        </TableHeader>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.cells.map((cell) => (
                        <TableCell
                          key={cell.id}
                          className={
                            cell.id.includes("actions") ? "rightAlign" : ""
                          }
                        >
                          {cell.value}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {allRows.length > 3 && (
                <Pagination
                  backwardText={`${t("main.tableText.pagination.backwardText")}`}
                  forwardText={`${t("main.tableText.pagination.forwardText")}`}
                  itemsPerPageText={`${t(
                    "main.tableText.pagination.itemsPerPageText",
                  )}`}
                  page={pagination.page}
                  onChange={({ page, pageSize }) => {
                    setPagination((prev) => ({
                      ...prev,
                      page: page,
                      pageSize: pageSize,
                    }));
                  }}
                  pageNumberText={`${t(
                    "main.tableText.pagination.pageNumberText",
                  )}`}
                  itemRangeText={(min, max, total) =>
                    `${min}–${max} ${t(
                      "main.tableText.pagination.range",
                    )} ${total} ${
                      total === 1
                        ? t("main.tableText.pagination.itemRangeText.item")
                        : t("main.tableText.pagination.itemRangeText.items")
                    }`
                  }
                  pageRangeText={(current, total) =>
                    `${t("main.tableText.pagination.range")} ${total} ${
                      total === 1
                        ? t("main.tableText.pagination.pageRangeText.page")
                        : t("main.tableText.pagination.pageRangeText.pages")
                    }`
                  }
                  pageSize={pagination.pageSize}
                  pageSizes={[3, 5, 10]}
                  size="md"
                  totalItems={allRows.length}
                />
              )}
            </TableContainer>
          )}
        />
      )}

      {!pending && openAddChatModal && (
        <AddItemModal
          openModal={openAddChatModal}
          setOpenModal={setOpenAddChatModal}
          table={"chat"}
          data={names}
          guid={guid}
        />
      )}
      {openEditChatModal && (
        <ChatMembersModal
          openModal={openEditChatModal}
          setOpenModal={setOpenEditChatModal}
          chatId={editChatId}
          setEditChatId={setEditChatId}
          setEditChatName={setEditChatName}
          editChatName={editChatName}
          guid={guid}
        />
      )}
      {openChatRemoval && (
        <DeleteItemModal
          open={openChatRemoval}
          setOpen={setOpenChatRemoval}
          type="chat"
          data={removingChat}
        />
      )}
    </div>
  );
}
